<template>
  <b-modal
    :title="$t('translationManager.createNewDialog.title')"
    body-class="create-translation-project-modal"
    size="lg"
    v-model="localShow"
    header-close-content=""
    @show="onShow"
    @ok="onConfirm"
    @cancel="onCancel">
    <div class="create-translation-project-container">
      <b-form-group :label="$t('name')">
        <b-input
          v-model="vm.data.projectName"
          v-tooltip="validationFeedbacks.projectName.feedback"
          :class="{ 'border-danger': !validationFeedbacks.projectName.isValid }" />
      </b-form-group>
      <b-form-group>
        <template #label>
          {{ $t("Catalogue") }}
        </template>
        <div class="catalog-selector-container">
          <Dropdown
            v-model="vm.data.catalogId"
            :items="sortedCatalogs || []"
            value-field="catalogId"
            text-field="name"
            class="catalog-selector-dropdown"
            :default="`<${$t('none')}>`"
            @input="selectCatalog">
            <template #item-template="{ entry }">
              <span v-if="entry" :class="{ 'font-italic': !entry.name && entry.fallbackName }"> {{ entry.name ? entry.name : entry.fallbackName }} </span>
            </template>
          </Dropdown>
          <b-btn class="ml-1 btn-icon mono" v-tooltip="$t('OpenCatalog')" v-b-modal.open-catalog><Icon icon="Open" /></b-btn>
        </div>
      </b-form-group>
      <div class="spinner-container" v-show="vm.isLoading"><Spinner /></div>
      <div class="translation-group-selection-container" v-show="!vm.isLoading">
        <template v-for="group in vm.data.groups" :key="group.type">
          <b-form-checkbox :disabled="group.entryCount === 0" v-model="group.enabled"
            >{{ $t(`translationManager.groups.${group.type}`) }} ({{ group.entryCount }})</b-form-checkbox
          >
        </template>
      </div>

      <div class="translation-group-selection-count" v-show="!vm.isLoading">
        <b-badge v-if="!validationFeedbacks.groups.isValid" class="translation-group-badge" :variant="getVariant(validationFeedbacks.groups.type)" pill>
          {{ validationFeedbacks.groups.feedback }}
        </b-badge>
        <b-badge v-else class="translation-group-badge" variant="light" pill>
          {{ $t("translationManager.createNewDialog.valueCount", { count: translationGroupEntryCount }) }}
        </b-badge>
      </div>
      <div class="language-selection-container">
        <b-form-group :label="$t('translationManager.sourceLanguage')" class="language-selector">
          <Dropdown
            v-model="vm.data.sourceLanguageId"
            :items="sourceLanguages || []"
            value-field="languageId"
            text-field="displayName"
            class="language-selector-dropdown"
            :default="`<${$t('none')}>`"
            @input="selectSourceLanguage">
            <template #item-template="{ entry }">
              <span v-if="entry"> {{ entry.displayName }} </span>
            </template>
          </Dropdown>
        </b-form-group>
        <b-form-group :label="$t('translationManager.targetLanguage')" class="language-selector">
          <Dropdown
            v-model="vm.data.targetLanguageId"
            :items="targetLanguages || []"
            value-field="languageId"
            text-field="displayName"
            class="language-selector-dropdown"
            :default="`<${$t('none')}>`"
            @input="selectTargetLanguage">
            <template #item-template="{ entry }">
              <span v-if="entry"> {{ entry.displayName }} </span>
            </template>
          </Dropdown>
        </b-form-group>
      </div>
      <b-form-checkbox v-model="vm.data.showOnlyOutdated">{{ $t("translationManager.createNewDialog.outdatedTranslations") }}</b-form-checkbox>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" variant="primary" :disabled="hasValidationErrorInternal || !canCreate">{{ $t("OK") }}</b-btn>
      <b-btn @click="cancel">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
  <ManageCatalogs @selectedCatalog="selectCatalog" modal-name="open-catalog" :includedCatalogTypes="[0, 1]" dialogMode="open"></ManageCatalogs>
</template>

<script setup lang="ts">
import { getLanguageDisplayName } from "@/util/languages";
import ManageCatalogs from "@ChannelCreator/components/ManageCatalogs.vue";
import Dropdown from "@common/components/Dropdown.vue";
import Icon from "@common/components/Icon.vue";
import Spinner from "@common/components/Spinner.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { ValidationFeedbackType } from "@medapro-types/common/common-enum";
import { CreateTranslationProjectData, TranslationGroupType } from "@medapro-types/TranslationManager/translationManager";
import translationManagerApi from "@services/translationManager";
import { useTranslationManagerState } from "@TranslationManager/composables/useTranslationManagerStore";
import { generateNewName } from "@util/generateNewName";
import { naturalSort } from "@util/naturalSort";
import ruleEvaluator from "@util/validations/ruleEvaluator";
import _ from "lodash";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

//#region General
const props = withDefaults(
  defineProps<{
    show: boolean;
    translationProjectsNames: string[];
  }>(),
  {}
);
const emits = defineEmits(["update:show", "confirm", "cancel"]);

const { t: $t } = useI18n();

const warningThreshold = 100000;
//#endregion

//#region Store
const { catalogs } = useTranslationManagerState();
//#endregion

//#region Languages
const languages = computed(() => {
  return contentLanguages.value.available.map((lang) => {
    return { languageId: lang.languageId, displayName: getLanguageDisplayName(lang.languageCode) };
  });
});
//#endregion

//#region Store
const { contentLanguages } = useContentLanguages();
//#endregion

//#region ViewModel
type ViewModel = { data: CreateTranslationProjectData; isLoading: boolean };

const vm = reactive<ViewModel>({
  data: {
    projectName: "",
    catalogId: null,
    sourceLanguageId: null,
    targetLanguageId: null,
    groups: [],
    showOnlyOutdated: false
  },
  isLoading: false
});
//#endregion

//#region Translation group selection
const updateGroupCounts = async () => {
  if (!vm.data.catalogId || !vm.data.sourceLanguageId || !vm.data.targetLanguageId) {
    return;
  }

  vm.isLoading = true;

  try {
    const sourceLanguageCode = contentLanguages.value.available.find((lang) => lang.languageId === vm.data.sourceLanguageId)?.languageCode;
    const targetLanguageCode = contentLanguages.value.available.find((lang) => lang.languageId === vm.data.targetLanguageId)?.languageCode;
    if (!sourceLanguageCode || !targetLanguageCode) {
      return;
    }

    const getEnabled = (groupType: TranslationGroupType) => {
      const previousGroup = vm.data.groups.find((prev) => prev.type === groupType);
      if (previousGroup) {
        return previousGroup.enabled;
      }

      return true;
    };
    const groupCounts = await translationManagerApi.getValueCounts(vm.data.catalogId, sourceLanguageCode, targetLanguageCode, vm.data.showOnlyOutdated);
    const newGroups = [
      {
        type: TranslationGroupType.ArticleDescriptions,
        entryCount: groupCounts.articleDescriptionCount ?? 0,
        enabled: getEnabled(TranslationGroupType.ArticleDescriptions)
      },
      {
        type: TranslationGroupType.ArticleNames,
        entryCount: groupCounts.articleNameCount ?? 0,
        enabled: getEnabled(TranslationGroupType.ArticleNames)
      },
      {
        type: TranslationGroupType.AttributeUnits,
        entryCount: groupCounts.attributeUnitCount ?? 0,
        enabled: getEnabled(TranslationGroupType.AttributeUnits)
      },
      {
        type: TranslationGroupType.AttributeValues,
        entryCount: groupCounts.attributeValueCount ?? 0,
        enabled: getEnabled(TranslationGroupType.AttributeValues)
      },
      {
        type: TranslationGroupType.CatalogGroupAttributes,
        entryCount: groupCounts.catalogNodeAttributeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupAttributes)
      },
      {
        type: TranslationGroupType.CatalogGroupDescriptions,
        entryCount: groupCounts.catalogNodeDescriptionCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupDescriptions)
      },
      {
        type: TranslationGroupType.CatalogGroupNames,
        entryCount: groupCounts.catalogNodeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupNames)
      },
      {
        type: TranslationGroupType.GlobalAttributeUnits,
        entryCount: groupCounts.globalAttributeUnitCount ?? 0,
        enabled: getEnabled(TranslationGroupType.GlobalAttributeUnits)
      },
      {
        type: TranslationGroupType.GlobalAttributeValues,
        entryCount: groupCounts.globalArticleAttributeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.GlobalAttributeValues)
      },
      {
        type: TranslationGroupType.Keywords,
        entryCount: groupCounts.keywordCount ?? 0,
        enabled: getEnabled(TranslationGroupType.Keywords)
      },
      {
        type: TranslationGroupType.VariationGroups,
        entryCount: groupCounts.variationGroupCount ?? 0,
        enabled: getEnabled(TranslationGroupType.VariationGroups)
      }
    ];

    vm.data.groups = newGroups;
  } catch (error) {
    console.error(error);
  } finally {
    vm.isLoading = false;
  }
};

const translationGroupEntryCount = computed(() => {
  return _.chain(vm.data.groups)
    .filter((group) => group.enabled)
    .sumBy((group) => group.entryCount)
    .value();
});
//#endregion

//#region Catalog selection
const sortedCatalogs = computed(() => {
  return naturalSort(catalogs.value, "name");
});

const selectCatalog = async (catalog: string | undefined) => {
  vm.data.catalogId = catalog ?? null;
  await updateGroupCounts();
};
//#endregion

//#region Language selection
const sourceLanguages = computed(() => {
  return languages.value;
});

const targetLanguages = computed(() => {
  return languages.value.filter((language) => !vm.data.sourceLanguageId || vm.data.sourceLanguageId !== language.languageId);
});

const selectSourceLanguage = async (languageId: number | undefined) => {
  if (vm.data.targetLanguageId === languageId) {
    vm.data.targetLanguageId = vm.data.sourceLanguageId;
  }

  vm.data.sourceLanguageId = languageId ?? null;
  await updateGroupCounts();
};

const selectTargetLanguage = async (languageId: number | undefined) => {
  vm.data.targetLanguageId = languageId ?? null;
  await updateGroupCounts();
};
//#endregion

//#region Show only outdated handling
watch(
  () => vm.data.showOnlyOutdated,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await updateGroupCounts();
    }
  }
);
//#endregion

//#region Validation
const getVariant = (type: ValidationFeedbackType) => {
  switch (type) {
    case ValidationFeedbackType.warning:
      return "warning";
    case ValidationFeedbackType.success:
      return "info";
    default:
      return "danger";
  }
};

const calculateTranslationGroupEntryCount = (data: CreateTranslationProjectData) => {
  return _.chain(data.groups)
    .filter((group) => group.enabled)
    .sumBy((group) => group.entryCount)
    .value();
};

const validationFeedbacks = computed(() => {
  const rules = {
    projectName: [
      {
        rule: (entry: CreateTranslationProjectData) => !entry.projectName,
        msg: $t("validation.valueRequired", { field: $t("name") }),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) =>
          entry.projectName !== null &&
          props.translationProjectsNames.some((x) => {
            return x.toLowerCase() == vm.data.projectName?.toLowerCase();
          }),
        msg: $t("validation.duplicateExists", {
          field: $t("name")
        }),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => (entry.projectName ? entry.projectName.length > 128 : false),
        msg: $t("validation.lengthExceeds", {
          field: $t("name", 1),
          length: 128
        }),
        severity: ValidationFeedbackType.error
      }
    ],
    groups: [
      {
        rule: (entry: CreateTranslationProjectData) => !_.some(entry.groups, (group) => group.entryCount > 0),
        msg: $t("translationManager.createNewDialog.noGroupValuesWarning"),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => calculateTranslationGroupEntryCount(entry) === 0,
        msg: $t("translationManager.createNewDialog.noSelectedGroupWarning"),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => calculateTranslationGroupEntryCount(entry) > warningThreshold,
        msg: $t("translationManager.createNewDialog.valueCountWarning", { count: warningThreshold }),
        severity: ValidationFeedbackType.warning
      }
    ]
  };

  const feedbacks = ruleEvaluator(vm.data, rules);
  return feedbacks;
});

const hasValidationErrorInternal = computed(() => {
  return Object.values(validationFeedbacks.value).some((feedback) => feedback.type === ValidationFeedbackType.error);
});
//#endregion

//#region Modal handling
const canCreate = computed(() => {
  return contentLanguages.value.available.length > 1 && vm.data.sourceLanguageId && vm.data.targetLanguageId && translationGroupEntryCount.value > 0;
});

const localShow = computed<boolean>({
  get: () => props.show,
  set: (nv) => emits("update:show", nv)
});

const onCancel = () => {
  emits("cancel", true);
};

const onShow = async () => {
  const projectNames = props.translationProjectsNames.map((name) => {
    return { name };
  });
  vm.data.projectName = generateNewName($t("translationManager.createNewDialog.defaultProjectName"), projectNames);

  if (sortedCatalogs.value.length > 0) {
    vm.data.catalogId = sortedCatalogs.value[0].catalogId;
  }

  if (sourceLanguages.value.length > 0) {
    vm.data.sourceLanguageId = sourceLanguages.value[0].languageId;
  }

  if (targetLanguages.value.length > 0) {
    vm.data.targetLanguageId = targetLanguages.value[0].languageId;
  }

  await updateGroupCounts();
};

const onConfirm = () => {
  localShow.value = false;
  emits("confirm", vm.data);
};
//#endregion
</script>
